import IDMGlobals from '../IDMGlobals';
import IDMUtils from '../IDMUtils';
//
const sha1 = require('sha1');
const forge = require('node-forge');
const rsa = forge.pki.rsa;

/**
* @class AppIDv2
* @classdesc AppIDv2 Class is reponsible for encoding and decoding the AppIDv2 token hash
* and also has utlities methods for its validation.
*/
export default class AppIDv2 {

  /**
   * constructor - Initializes a new AppID with already encoded token,
   * so you should use one instance of appid per usage.
   *
   * @param  {type} appID  The appid hash if initializing the class for decoding.
   * @param  {type} token  The application token if initializng th class for encoding.
   * @param  {type} secret The application secret if initializng th class for encoding.
   */
  constructor(appID, token, secret, keys) {
    //decoded data
    this.verificationObject = null;
    //encoded data
    this.hash = null;
    //pre encode
    this.accessSecret = null;
    this.accessToken = null;
    //decode/encode ops auxs
    this.publicKey = null;
    this.privateKey = null;
    //init with appID to decode
    if (IDMUtils.isValidString(appID)) {
      this.hash = appID;
      this.privateKey = keys;
      this._decode();
    }
    //Init with token and secret to encode
    else if (IDMUtils.isValidString(token) && IDMUtils.isValidString(secret)) {
      this.accessToken = token;
      this.accessSecret = secret;
      this.publicKey = keys;
      this._encode();
    }
  }
  /**
   * getHash - Get current AppID hash token used with communication with the API.
   *
   * @return {string}  The AppID token hash. If initialized with the token to be decoded, hash
   * is expected to be the same value of the initialization.
   */
  getHash() { return this.hash; } //encoded app id

  /**
   * getVerificationToken - Get verification code generated by the token or by decoding the hash.
   *
   * @return {string}  The verification code hash.
   */
  getVerificationToken() { return (this.verificationObject && this.verificationObject.token ? this.verificationObject.token : null); }

  /**
   * _decode - Internal decode this.hash and set internal ivars.
   * @private
   */
  _decode() {
    //decode
    let obj = null;
    try {
      obj = JSON.parse(this.privateKey.decrypt(forge.util.decode64(this.hash), 'RSAES-PKCS1-V1_5'));
    } catch(e) {
      console.log(e);
    }
    //Validate
    if (obj && IDMUtils.isValidString(obj.token)) this.verificationObject = obj;
  }

  /**
   * _encode - Internally encode and set the object ivars.
   * @private
   */
  _encode() {
    const verificationToken = AppIDv2.verificationCode(this.accessToken, this.accessSecret);
    this.verificationObject = { token: verificationToken, timestamp: Date.now() /*include for some randomness on the result :p */ };
    // encrypt data with a public key using RSAES PKCS#1 v1.5
    this.hash = forge.util.encode64(this.publicKey.encrypt(JSON.stringify(this.verificationObject), 'RSAES-PKCS1-V1_5'));
  }
}

/**
 * verificationCode - Get verification code by token key and secret.
 *
 * @param  {string} token  description
 * @param  {string} secret description
 * @return {string}        Verificaiton code of the token key and secret.
 */
AppIDv2.verificationCode = function(token, secret) {
  return sha1(JSON.stringify({token, secret}))
}
//RSA PCK
AppIDv2.generateKeyPair = async function() {
  return new Promise((resolve, reject) => {
    rsa.generateKeyPair({ bits: 2048, workers: -1 }, function (err, keypair) {
      if (err) {
        reject(err);
        return;
      }
      const pbk = forge.pki.publicKeyToPem(keypair.publicKey);
      const prk = forge.pki.encryptRsaPrivateKey(keypair.privateKey, pbk);
      resolve({ publicKey: pbk, privateKey: prk });
    });
  });
}
AppIDv2.loadPublicKey = function (publicKey) {  return forge.pki.publicKeyFromPem(publicKey); }
AppIDv2.loadPrivateKey = function (privateKey, publicKey) { return forge.pki.decryptRsaPrivateKey(privateKey, publicKey); }